import { FormEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DEFAULT_TASK_NOTIFICATION_TEMPLATE,
  SalesOpportunity,
  SalesOpportunityTask,
  SalesOpportunityTaskUpsertPayload,
  SalesOpportunityTeamMember,
  TASK_MACRO_HINTS,
} from '../../services/salesOpportunitiesApi';
import { formatOpportunityStageLabel } from './OpportunityKanbanBoard';
import { RequiredMark } from '../ui/RequiredMark';

const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

const WEEKDAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

const SERIES_PREVIEW_COUNT = 4;

type TaskRow =
  | { kind: 'task'; task: SalesOpportunityTask; showStop: boolean }
  | { kind: 'more'; seriesId: string; remaining: number };

function weekdayFromLocalInput(value: string): number | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getDay();
}

function recurrenceWindowHint(type: string, indefinite: boolean): string {
  if (type === 'None') return '';
  const windowLabel = type === 'Daily'
    ? '90 dias'
    : type === 'Monthly'
      ? '24 meses'
      : type === 'Annual'
        ? '5 anos'
        : '12 meses';
  if (indefinite) {
    return `As ocorrências dos próximos ${windowLabel} são criadas agora. Depois disso, o sistema continua gerando automaticamente.`;
  }
  return 'As ocorrências até o prazo informado são criadas agora, no máximo 400.';
}

function formatRecurrenceLabel(task: SalesOpportunityTask): string | null {
  if (!task.recurrenceSeriesId || !task.recurrenceType || task.recurrenceType === 'None') {
    return null;
  }
  const until = task.recurrenceIndefinite || !task.recurrenceUntilDate
    ? 'prazo indefinido'
    : `até ${task.recurrenceUntilDate.split('-').reverse().join('/')}`;
  const dayNames = (task.recurrenceDaysOfWeek ?? '')
    .split(',')
    .map((token) => WEEKDAY_NAMES[Number(token)])
    .filter(Boolean);
  switch (task.recurrenceType) {
    case 'Daily':
      return `Diária · ${until}`;
    case 'Weekly':
      return `Semanal${dayNames.length ? `: ${dayNames.join(', ')}` : ''} · ${until}`;
    case 'Weekdays':
      return `Dias da semana${dayNames.length ? `: ${dayNames.join(', ')}` : ''} · ${until}`;
    case 'Monthly':
      return `Mensal · ${until}`;
    case 'Annual':
      return `Anual · ${until}`;
    default:
      return null;
  }
}

function buildTaskRows(tasks: SalesOpportunityTask[], expanded: ReadonlySet<string>): TaskRow[] {
  const counts = new Map<string, number>();
  tasks.forEach((task) => {
    if (!task.recurrenceSeriesId) return;
    counts.set(task.recurrenceSeriesId, (counts.get(task.recurrenceSeriesId) ?? 0) + 1);
  });

  const shown = new Map<string, number>();
  const stopShown = new Set<string>();
  const rows: TaskRow[] = [];

  tasks.forEach((task) => {
    const seriesId = task.recurrenceSeriesId ?? undefined;
    const total = seriesId ? counts.get(seriesId) ?? 0 : 0;
    const collapsed = Boolean(seriesId) && total > SERIES_PREVIEW_COUNT && !expanded.has(seriesId!);
    if (collapsed && seriesId) {
      const count = shown.get(seriesId) ?? 0;
      if (count >= SERIES_PREVIEW_COUNT) return;
      shown.set(seriesId, count + 1);
    }

    const showStop = Boolean(task.recurrenceActive && seriesId && !stopShown.has(seriesId));
    if (showStop && seriesId) stopShown.add(seriesId);
    rows.push({ kind: 'task', task, showStop });

    if (collapsed && seriesId && (shown.get(seriesId) ?? 0) === SERIES_PREVIEW_COUNT) {
      rows.push({
        kind: 'more',
        seriesId,
        remaining: total - SERIES_PREVIEW_COUNT,
      });
    }
  });

  return rows;
}

type OpportunityDetailPanelProps = {
  opportunity: SalesOpportunity | null;
  teamMembers: SalesOpportunityTeamMember[];
  canWrite: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => Promise<void>;
  onCreateTask: (payload: SalesOpportunityTaskUpsertPayload) => Promise<void>;
  onCompleteTask: (taskId: string) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onStopRecurrence: (taskId: string) => Promise<void>;
};

function fromLocalDateTimeInput(value: string): string {
  return new Date(value).toISOString();
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatEngagementStatus(status?: string | null): string {
  switch (status) {
    case 'AwaitingClient':
      return 'Sem contato';
    case 'Paused':
      return 'Pausada';
    default:
      return 'Ativa';
  }
}

function formatPricingNature(nature: string): string {
  switch (nature) {
    case 'Development':
      return 'Desenvolvimento';
    case 'Recurring':
      return 'Recorrente';
    default:
      return 'Setup';
  }
}

export function OpportunityDetailPanel({
  opportunity,
  teamMembers,
  canWrite,
  onClose,
  onEdit,
  onDelete,
  onCreateTask,
  onCompleteTask,
  onDeleteTask,
  onStopRecurrence,
}: OpportunityDetailPanelProps) {
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [assignedToUserId, setAssignedToUserId] = useState('');
  const [dueAtLocal, setDueAtLocal] = useState('');
  const [notifyInApp, setNotifyInApp] = useState(true);
  const [notifyByEmail, setNotifyByEmail] = useState(false);
  const [reminderMode, setReminderMode] = useState('None');
  const [offsetDays, setOffsetDays] = useState(1);
  const [offsetHours, setOffsetHours] = useState(0);
  const [offsetMinutes, setOffsetMinutes] = useState(0);
  const [reminderSpecificLocal, setReminderSpecificLocal] = useState('');
  const [notificationTemplate, setNotificationTemplate] = useState(DEFAULT_TASK_NOTIFICATION_TEMPLATE);
  const [recurrenceType, setRecurrenceType] = useState('None');
  const [recurrenceDays, setRecurrenceDays] = useState<number[]>([]);
  const [recurrenceIndefinite, setRecurrenceIndefinite] = useState(true);
  const [recurrenceUntilDate, setRecurrenceUntilDate] = useState('');
  const [taskError, setTaskError] = useState<string | null>(null);
  const [expandedSeries, setExpandedSeries] = useState<string[]>([]);
  const [savingTask, setSavingTask] = useState(false);

  useEffect(() => {
    if (!opportunity) return;
    setShowTaskForm(false);
    setAssignedToUserId(opportunity.ownerUserId ?? teamMembers[0]?.id ?? '');
    setRecurrenceType('None');
    setRecurrenceDays([]);
    setRecurrenceIndefinite(true);
    setRecurrenceUntilDate('');
    setTaskError(null);
    setExpandedSeries([]);
  }, [opportunity, teamMembers]);

  if (!opportunity) return null;

  async function handleCreateTask(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!dueAtLocal || !assignedToUserId) return;

    if ((recurrenceType === 'Weekly' || recurrenceType === 'Weekdays') && recurrenceDays.length === 0) {
      setTaskError('Selecione ao menos um dia da semana.');
      return;
    }
    if (recurrenceType !== 'None' && !recurrenceIndefinite && !recurrenceUntilDate) {
      setTaskError('Informe o prazo da recorrência ou marque como indefinido.');
      return;
    }
    if (
      (recurrenceType === 'Weekly' || recurrenceType === 'Weekdays')
      && recurrenceDays.length > 0
    ) {
      const dueWeekday = weekdayFromLocalInput(dueAtLocal);
      if (dueWeekday != null && !recurrenceDays.includes(dueWeekday)) {
        setTaskError('A data de execução não cai em um dos dias da recorrência. Ajuste a data ou os dias.');
        return;
      }
    }

    const payload: SalesOpportunityTaskUpsertPayload = {
      title: taskTitle,
      description: taskDescription || undefined,
      assignedToUserId,
      dueAtUtc: fromLocalDateTimeInput(dueAtLocal),
      notifyInApp,
      notifyByEmail,
      reminderMode,
      notificationMessageTemplate: notificationTemplate,
      recurrenceType,
      recurrenceIndefinite: recurrenceType !== 'None' && recurrenceIndefinite,
      scheduleOffsetMinutes: new Date(dueAtLocal).getTimezoneOffset(),
    };
    if (recurrenceType === 'Weekly' || recurrenceType === 'Weekdays') {
      payload.recurrenceDaysOfWeek = [...recurrenceDays].sort((left, right) => left - right).join(',');
    }
    if (recurrenceType !== 'None' && !recurrenceIndefinite) {
      payload.recurrenceUntilDate = recurrenceUntilDate;
    }
    setTaskError(null);

    if (reminderMode === 'BeforeDueOffset') {
      payload.reminderOffsetMinutes = offsetDays * 1440 + offsetHours * 60 + offsetMinutes;
    } else if (reminderMode === 'SpecificDateTime' && reminderSpecificLocal) {
      payload.reminderSpecificAtUtc = fromLocalDateTimeInput(reminderSpecificLocal);
    }

    setSavingTask(true);
    try {
      await onCreateTask(payload);
      setShowTaskForm(false);
      setTaskTitle('');
      setTaskDescription('');
      setRecurrenceType('None');
      setRecurrenceDays([]);
      setRecurrenceIndefinite(true);
      setRecurrenceUntilDate('');
    } finally {
      setSavingTask(false);
    }
  }

  const pendingTasks = [...opportunity.tasks.filter((task) => task.status === 'Pending')]
    .sort((left, right) => left.dueAtUtc.localeCompare(right.dueAtUtc));
  const completedTasks = [...opportunity.tasks.filter((task) => task.status !== 'Pending')]
    .sort((left, right) => left.dueAtUtc.localeCompare(right.dueAtUtc));
  const expanded = new Set(expandedSeries);

  return createPortal(
    <div className="fixed inset-0 z-[80] m-0 flex justify-end bg-black/30">
      <aside className="m-0 flex h-full w-full max-w-lg flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-secondary-200 p-4">
          <div className="min-w-0 pr-3">
            <p className="text-xs font-semibold uppercase text-secondary-500">
              {formatOpportunityStageLabel(opportunity.stage)}
            </p>
            <h2 className="erp-page-title text-xl">{opportunity.title}</h2>
            <p className="text-sm text-secondary-600">
              {opportunity.companyName
                ?? opportunity.clientName
                ?? opportunity.prospectCompany
                ?? 'Sem empresa'}
            </p>
          </div>
          <button type="button" className="erp-btn-secondary px-3 py-1" onClick={onClose}>
            Fechar
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-secondary-500">Valor</p>
              <p className="font-semibold">{formatCurrency(opportunity.amount)}</p>
            </div>
            <div>
              <p className="text-secondary-500">MRR</p>
              <p className="font-semibold">{formatCurrency(opportunity.monthlyRecurringAmount)}</p>
            </div>
            <div>
              <p className="text-secondary-500">Responsável</p>
              <p className="font-semibold">{opportunity.ownerUserName ?? '—'}</p>
            </div>
            <div>
              <p className="text-secondary-500">Probabilidade</p>
              <p className="font-semibold">{opportunity.probabilityPercent}%</p>
            </div>
            <div>
              <p className="text-secondary-500">Acompanhamento</p>
              <p className="font-semibold">
                {formatEngagementStatus(opportunity.engagementStatus)}
              </p>
            </div>
            {opportunity.nextContactAtUtc ? (
              <div>
                <p className="text-secondary-500">Próximo contato</p>
                <p className="font-semibold">
                  {new Date(opportunity.nextContactAtUtc).toLocaleDateString('pt-BR')}
                </p>
              </div>
            ) : null}
          </div>

          {opportunity.pricingLines?.length ? (
            <div className="rounded-lg bg-secondary-50 p-3 text-sm">
              <p className="mb-2 font-medium text-secondary-800">Precificação</p>
              <ul className="space-y-1">
                {opportunity.pricingLines.map((line) => (
                  <li key={line.id} className="flex justify-between gap-2">
                    <span>{formatPricingNature(line.nature)}</span>
                    <span className="font-medium">{formatCurrency(line.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {opportunity.prospectName ? (
            <div className="rounded-lg bg-secondary-50 p-3 text-sm">
              <p className="font-medium text-secondary-800">Prospect vinculado</p>
              <p>{opportunity.prospectName}</p>
              {opportunity.prospectCompany ? (
                <p className="text-secondary-600">{opportunity.prospectCompany}</p>
              ) : null}
            </div>
          ) : null}

          {canWrite ? (
            <div className="flex flex-col gap-2">
              <button type="button" className="erp-btn-secondary w-full" onClick={onEdit}>
                Editar oportunidade
              </button>
              <button
                type="button"
                className="erp-btn-danger w-full"
                disabled={deleting}
                onClick={() => {
                  void (async () => {
                    setDeleting(true);
                    try {
                      await onDelete();
                    } finally {
                      setDeleting(false);
                    }
                  })();
                }}
              >
                {deleting ? 'Excluindo...' : 'Excluir oportunidade'}
              </button>
            </div>
          ) : null}

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-secondary-900">Tarefas</h3>
              {canWrite ? (
                <button
                  type="button"
                  className="erp-btn-primary px-3 py-1 text-xs"
                  onClick={() => setShowTaskForm((current) => !current)}
                >
                  {showTaskForm ? 'Cancelar' : 'Nova tarefa'}
                </button>
              ) : null}
            </div>

            {showTaskForm ? (
              <form onSubmit={(event) => void handleCreateTask(event)} className="erp-card">
                <div className="erp-card-body erp-form">
                  <label className="erp-label">
                    Título <RequiredMark />
                    <input
                      className="erp-input"
                      value={taskTitle}
                      onChange={(e) => setTaskTitle(e.target.value)}
                      required
                    />
                  </label>
                  <label className="erp-label">
                    Descrição
                    <textarea
                      className="erp-input min-h-[60px]"
                      value={taskDescription}
                      onChange={(e) => setTaskDescription(e.target.value)}
                    />
                  </label>
                  <label className="erp-label">
                    Responsável <RequiredMark />
                    <select
                      className="erp-input"
                      value={assignedToUserId}
                      onChange={(e) => setAssignedToUserId(e.target.value)}
                      required
                    >
                      {teamMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="erp-label">
                    Data de execução <RequiredMark />
                    <input
                      type="datetime-local"
                      className="erp-input"
                      value={dueAtLocal}
                      onChange={(e) => setDueAtLocal(e.target.value)}
                      required
                    />
                  </label>
                  <label className="erp-label">
                    Recorrência
                    <select
                      className="erp-input"
                      value={recurrenceType}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRecurrenceType(value);
                        setTaskError(null);
                        if (value === 'Weekly') {
                          const day = weekdayFromLocalInput(dueAtLocal);
                          setRecurrenceDays(day == null ? [] : [day]);
                        } else if (value === 'Weekdays') {
                          setRecurrenceDays([]);
                        }
                        if (value !== 'None') setRecurrenceIndefinite(true);
                      }}
                    >
                      <option value="None">Não repetir</option>
                      <option value="Daily">Diária</option>
                      <option value="Weekly">Semanal</option>
                      <option value="Monthly">Mensal</option>
                      <option value="Annual">Anual</option>
                      <option value="Weekdays">Dias da semana</option>
                    </select>
                  </label>
                  {recurrenceType === 'Weekly' || recurrenceType === 'Weekdays' ? (
                    <div>
                      <p className="erp-label">Dias da semana</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {WEEKDAY_OPTIONS.map((day) => {
                          const selected = recurrenceDays.includes(day.value);
                          return (
                            <button
                              key={day.value}
                              type="button"
                              className={selected ? 'erp-btn-primary erp-btn-sm' : 'erp-btn-secondary erp-btn-sm'}
                              onClick={() => {
                                setRecurrenceDays((current) => (
                                  current.includes(day.value)
                                    ? current.filter((item) => item !== day.value)
                                    : [...current, day.value]
                                ));
                                setTaskError(null);
                              }}
                            >
                              {day.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {recurrenceType !== 'None' ? (
                    <>
                      <label className="erp-label-inline">
                        <input
                          type="checkbox"
                          checked={recurrenceIndefinite}
                          onChange={(e) => setRecurrenceIndefinite(e.target.checked)}
                        />
                        Prazo indefinido
                      </label>
                      {recurrenceIndefinite ? null : (
                        <label className="erp-label">
                          Repetir até <RequiredMark />
                          <input
                            type="date"
                            className="erp-input"
                            value={recurrenceUntilDate}
                            min={dueAtLocal ? dueAtLocal.slice(0, 10) : undefined}
                            onChange={(e) => setRecurrenceUntilDate(e.target.value)}
                            required
                          />
                        </label>
                      )}
                      <p className="erp-hint">{recurrenceWindowHint(recurrenceType, recurrenceIndefinite)}</p>
                    </>
                  ) : null}
                  <label className="erp-label-inline">
                    <input
                      type="checkbox"
                      checked={notifyInApp}
                      onChange={(e) => setNotifyInApp(e.target.checked)}
                    />
                    Notificar no sistema
                  </label>
                  <label className="erp-label-inline">
                    <input
                      type="checkbox"
                      checked={notifyByEmail}
                      onChange={(e) => setNotifyByEmail(e.target.checked)}
                    />
                    Notificar por e-mail
                  </label>
                  <label className="erp-label">
                    Lembrete
                    <select
                      className="erp-input"
                      value={reminderMode}
                      onChange={(e) => setReminderMode(e.target.value)}
                    >
                      <option value="None">Sem lembrete</option>
                      <option value="BeforeDueOffset">Antes do vencimento</option>
                      <option value="SpecificDateTime">Data/hora específica</option>
                    </select>
                  </label>
                  {reminderMode === 'BeforeDueOffset' ? (
                    <div className="grid grid-cols-3 gap-2">
                      <label className="erp-label">
                        Dias
                        <input
                          type="number"
                          min={0}
                          className="erp-input"
                          value={offsetDays}
                          onChange={(e) => setOffsetDays(Number(e.target.value))}
                        />
                      </label>
                      <label className="erp-label">
                        Horas
                        <input
                          type="number"
                          min={0}
                          max={23}
                          className="erp-input"
                          value={offsetHours}
                          onChange={(e) => setOffsetHours(Number(e.target.value))}
                        />
                      </label>
                      <label className="erp-label">
                        Minutos
                        <input
                          type="number"
                          min={0}
                          max={59}
                          className="erp-input"
                          value={offsetMinutes}
                          onChange={(e) => setOffsetMinutes(Number(e.target.value))}
                        />
                      </label>
                    </div>
                  ) : null}
                  {reminderMode === 'SpecificDateTime' ? (
                    <label className="erp-label">
                      Disparar em
                      <input
                        type="datetime-local"
                        className="erp-input"
                        value={reminderSpecificLocal}
                        onChange={(e) => setReminderSpecificLocal(e.target.value)}
                      />
                    </label>
                  ) : null}
                  <label className="erp-label">
                    Mensagem (macros)
                    <textarea
                      className="erp-input min-h-[80px] font-mono text-xs"
                      value={notificationTemplate}
                      onChange={(e) => setNotificationTemplate(e.target.value)}
                    />
                  </label>
                  <p className="erp-hint">{TASK_MACRO_HINTS.join(' · ')}</p>
                  {taskError ? <p className="text-sm text-red-600">{taskError}</p> : null}
                  <button type="submit" disabled={savingTask} className="erp-btn-primary">
                    {savingTask ? 'Salvando...' : 'Criar tarefa'}
                  </button>
                </div>
              </form>
            ) : null}

            <TaskList
              tasks={pendingTasks}
              canWrite={canWrite}
              expanded={expanded}
              onToggleSeries={(seriesId) => {
                setExpandedSeries((current) => (
                  current.includes(seriesId)
                    ? current.filter((item) => item !== seriesId)
                    : [...current, seriesId]
                ));
              }}
              onComplete={onCompleteTask}
              onDelete={onDeleteTask}
              onStopRecurrence={onStopRecurrence}
              emptyLabel="Nenhuma tarefa pendente."
            />
            {completedTasks.length > 0 ? (
              <>
                <p className="text-xs font-semibold uppercase text-secondary-500">Concluídas</p>
                <TaskList
                  tasks={completedTasks}
                  canWrite={canWrite}
                  expanded={expanded}
                  onToggleSeries={(seriesId) => {
                    setExpandedSeries((current) => (
                      current.includes(seriesId)
                        ? current.filter((item) => item !== seriesId)
                        : [...current, seriesId]
                    ));
                  }}
                  onComplete={onCompleteTask}
                  onDelete={onDeleteTask}
                  onStopRecurrence={onStopRecurrence}
                  emptyLabel=""
                />
              </>
            ) : null}
          </section>
        </div>
      </aside>
    </div>,
    document.body,
  );
}

type TaskListProps = {
  tasks: SalesOpportunityTask[];
  canWrite: boolean;
  expanded: ReadonlySet<string>;
  onToggleSeries: (seriesId: string) => void;
  onComplete: (taskId: string) => Promise<void>;
  onDelete: (taskId: string) => Promise<void>;
  onStopRecurrence: (taskId: string) => Promise<void>;
  emptyLabel: string;
};

function TaskList({
  tasks,
  canWrite,
  expanded,
  onToggleSeries,
  onComplete,
  onDelete,
  onStopRecurrence,
  emptyLabel,
}: TaskListProps) {
  if (tasks.length === 0) {
    return emptyLabel ? <p className="erp-empty">{emptyLabel}</p> : null;
  }

  const rows = buildTaskRows(tasks, expanded);

  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        if (row.kind === 'more') {
          return (
            <li key={`more-${row.seriesId}`}>
              <button
                type="button"
                className="text-sm font-medium text-secondary-700 underline"
                onClick={() => onToggleSeries(row.seriesId)}
              >
                Mostrar mais {row.remaining} ocorrências
              </button>
            </li>
          );
        }

        const { task, showStop } = row;
        const recurrenceLabel = formatRecurrenceLabel(task);
        return (
          <li key={task.id} className="rounded-lg border border-secondary-200 p-3 text-sm">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-secondary-900">{task.title}</p>
                <p className="text-secondary-600">
                  {task.assignedToUserName} —{' '}
                  {new Date(task.dueAtUtc).toLocaleString('pt-BR')}
                </p>
                {recurrenceLabel ? <p className="erp-hint">{recurrenceLabel}</p> : null}
                {task.reminderMode !== 'None' ? (
                  <p className="erp-hint">
                    Lembrete: {task.reminderMode === 'BeforeDueOffset' && task.reminderOffsetMinutes
                      ? `${task.reminderOffsetMinutes} min antes`
                      : task.reminderSpecificAtUtc
                        ? new Date(task.reminderSpecificAtUtc).toLocaleString('pt-BR')
                        : 'configurado'}
                    {task.reminderSentAtUtc ? ' (enviado)' : ''}
                  </p>
                ) : null}
                {canWrite && showStop ? (
                  <button
                    type="button"
                    className="erp-btn-secondary erp-btn-sm mt-2"
                    onClick={() => void onStopRecurrence(task.id)}
                  >
                    Encerrar recorrência
                  </button>
                ) : null}
              </div>
              {canWrite && task.status === 'Pending' ? (
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    className="erp-btn-primary erp-btn-sm"
                    onClick={() => void onComplete(task.id)}
                  >
                    Concluir
                  </button>
                  <button
                    type="button"
                    className="erp-btn-danger erp-btn-sm"
                    onClick={() => void onDelete(task.id)}
                  >
                    Excluir
                  </button>
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
